import axios from 'axios';

/**
 * الافتراضي دائماً استضافة hamzabenz (حتى عند npm run dev على الجهاز).
 * لتجربة Django محلياً: أنشئ .env.local وضع REACT_APP_API_URL=http://127.0.0.1:8000/api
 */
function resolveApiBaseUrl() {
  const env = (process.env.REACT_APP_API_URL || '').trim();
  if (env) return env.replace(/\/$/, '');
  return 'https://api.hamzabenz.ly/api';
}

function resolveMediaBaseUrl() {
  const env = (process.env.REACT_APP_MEDIA_URL || '').trim();
  if (env) return env.replace(/\/$/, '');
  return 'https://api.hamzabenz.ly';
}

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  config.baseURL = resolveApiBaseUrl();
  return config;
});

// إضافة interceptor لإضافة التوكن تلقائياً لطلبات المدير
api.interceptors.request.use(
  (config) => {
    // إذا كان الطلب لـ /admin/، أضف التوكن تلقائياً (لكن ليس لطلب تسجيل الدخول)
    if (config.url && config.url.startsWith('/admin/') && !config.url.includes('/auth/login')) {
      const token = localStorage.getItem('admin_token');
      if (token) {
        config.headers.Authorization = `Token ${token}`;
      }
    }
    
    // إذا كان الطلب FormData، احذف Content-Type للسماح للمتصفح بتعيينه تلقائياً
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// إضافة interceptor للتعامل مع أخطاء المصادقة
api.interceptors.response.use(
  (response) => {
    // التأكد من أن الاستجابة تحتوي على البيانات
    return response;
  },
  (error) => {
    // إذا كان الخطأ 401 (غير مصرح)، احذف التوكن وأعد التوجيه إلى صفحة تسجيل الدخول
    if (error.response && error.response.status === 401) {
      const isAdminRoute = error.config && error.config.url && error.config.url.startsWith('/admin/');
      if (isAdminRoute && !error.config.url.includes('/auth/login')) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        // تجنب إعادة التوجيه إذا كنا بالفعل في صفحة تسجيل الدخول
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// دالة مساعدة لإضافة URL الكامل للصور
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${resolveMediaBaseUrl()}${imagePath}`;
};

// Cars API
export const carsAPI = {
  getModels: () => api.get('/cars/models/'),
  getModel: (idOrSlug) => api.get(`/cars/models/${idOrSlug}/`),
  getModelVariants: (idOrSlug) => api.get(`/cars/models/${idOrSlug}/variants/`),
  getVariant: (idOrSlug) => api.get(`/cars/variants/${idOrSlug}/`),
  search: (q, limit) =>
    api.get('/cars/search/', { params: { q, ...(limit ? { limit } : {}) } }),
  getVariantSpareParts: (variantId, area) => {
    const params = {};
    if (area) params.area = area;
    return api.get(`/cars/variants/${variantId}/spare-parts/`, { params });
  },
  getVariantAccessories: (variantId, area) => {
    const params = {};
    if (area) params.area = area;
    return api.get(`/cars/variants/${variantId}/accessories/`, { params });
  },
};

// Parts API
export const partsAPI = {
  getPart: (id) => api.get(`/parts/parts/${id}/`),
  getPartSpareParts: (id) => api.get(`/parts/parts/${id}/spare-parts/`),
  getPartAccessories: (id) => api.get(`/parts/parts/${id}/accessories/`),
  getSparePart: (id) => api.get(`/parts/spare-parts/${id}/`),
};

// Settings API
export const settingsAPI = {
  getSettings: () => api.get('/settings/current/'),
  getHomeStats: () => api.get('/settings/stats/'),
};

/** طلبات العملاء (عام — إنشاء) */
export const ordersAPI = {
  create: (data) => api.post('/orders/create/', data),
};

// Admin API
export const adminAPI = {
  login: (username, password) => api.post('/admin/auth/login/', { username, password }),
  getStats: () => api.get('/admin/settings/stats/'),
  // Car Models
  getCarModels: () => api.get('/admin/car-models/'),
  createCarModel: (data) => api.post('/admin/car-models/', data),
  updateCarModel: (id, data) => api.patch(`/admin/car-models/${id}/`, data),
  deleteCarModel: (id) => api.delete(`/admin/car-models/${id}/`),
  reorderCarModels: (modelIds) => api.post('/admin/car-models/reorder/', { model_ids: modelIds }),
  // Car Variants
  getCarVariants: () => api.get('/admin/car-variants/'),
  createCarVariant: (data) => api.post('/admin/car-variants/', data),
  updateCarVariant: (id, data) => api.patch(`/admin/car-variants/${id}/`, data),
  deleteCarVariant: (id) => api.delete(`/admin/car-variants/${id}/`),
  reorderCarVariants: (variantIds) => api.post('/admin/car-variants/reorder/', { variant_ids: variantIds }),
  // Spare Parts (قطع الغيار)
  getSpareParts: (params) => api.get('/admin/spare-parts/', { params: params || {} }),
  getSparePartByBarcode: (barcode) => api.get('/admin/spare-parts/by-barcode/', { params: { barcode } }),
  createSparePart: (data) => api.post('/admin/spare-parts/', data),
  updateSparePart: (id, data) => api.patch(`/admin/spare-parts/${id}/`, data),
  deleteSparePart: (id) => api.delete(`/admin/spare-parts/${id}/`),
  reorderSpareParts: (sparePartIds) => api.post('/admin/spare-parts/reorder/', { spare_part_ids: sparePartIds }),
  addSparePartImage: (id, file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post(`/admin/spare-parts/${id}/images/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteSparePartImage: (sparePartId, imageId) => api.delete(`/admin/spare-parts/${sparePartId}/images/${imageId}/`),
  // Accessories (كماليات — جدول منفصل)
  getAccessories: (params) => api.get('/admin/accessories/', { params: params || {} }),
  getAccessoryByBarcode: (barcode) => api.get('/admin/accessories/by-barcode/', { params: { barcode } }),
  createAccessory: (data) => api.post('/admin/accessories/', data),
  updateAccessory: (id, data) => api.patch(`/admin/accessories/${id}/`, data),
  deleteAccessory: (id) => api.delete(`/admin/accessories/${id}/`),
  reorderAccessories: (accessoryIds) => api.post('/admin/accessories/reorder/', { accessory_ids: accessoryIds }),
  addAccessoryImage: (id, file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post(`/admin/accessories/${id}/images/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAccessoryImage: (accessoryId, imageId) => api.delete(`/admin/accessories/${accessoryId}/images/${imageId}/`),
  // Settings
  getSettings: () => api.get('/admin/settings/1/'),
  updateSettings: (data) => api.patch('/admin/settings/1/', data),
  // Analytics (لوحة المدير — Token)
  getAnalyticsOverview: (params) => api.get('/admin/analytics/overview/', { params: params || {} }),
  getAnalyticsTopPages: (params) => api.get('/admin/analytics/top-pages/', { params: params || {} }),
  getAnalyticsTopVariants: (params) => api.get('/admin/analytics/top-variants/', { params: params || {} }),
  getAnalyticsSources: (params) => api.get('/admin/analytics/sources/', { params: params || {} }),
  getAnalyticsDevices: (params) => api.get('/admin/analytics/devices/', { params: params || {} }),
  getAnalyticsTimeseries: (params) => api.get('/admin/analytics/timeseries/', { params: params || {} }),
  // الطلبات
  getOrders: () => api.get('/admin/orders/'),
  getOrder: (id) => api.get(`/admin/orders/${id}/`),
  patchOrderStatus: (id, status) => api.patch(`/admin/orders/${id}/`, { status }),
};

export default api;
